import React, { useCallback, useEffect, useState } from 'react';

// Care Desk: chaplain teams keep care records, notes, and follow-ups.
// Every chaplain request from the app arrives here as a record.
const API = window.location.origin + '/api/v1/care-desk';
const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });

const pad = (n) => String(n).padStart(2, '0');
const localISO = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const today = () => localISO(new Date());
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return localISO(d); };
const nice = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const when = (ts) => {
  if (!ts) return '';
  const d = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};
const dueInfo = (r) => {
  if (r.status === 'closed') return { label: 'Closed', tone: 'muted' };
  if (!r.next_followup) return { label: 'No follow-up set', tone: 'muted' };
  const t = today();
  if (r.next_followup < t) {
    const days = Math.round((new Date(t) - new Date(r.next_followup)) / 86400000);
    return { label: 'Overdue ' + days + (days === 1 ? ' day' : ' days'), tone: 'late' };
  }
  if (r.next_followup === t) return { label: 'Due today', tone: 'today' };
  return { label: 'Due ' + nice(r.next_followup), tone: 'later' };
};
const KINDS = [['call', 'Call'], ['visit', 'Visit'], ['text', 'Text'], ['email', 'Email'], ['prayer', 'Prayer'], ['note', 'Note']];
const KIND_LABEL = { call: 'Call', visit: 'Visit', text: 'Text', email: 'Email', prayer: 'Prayer', note: 'Note', request: 'Request', status: 'Update' };
const short = (email) => (email || '').split('@')[0];

const CSS = `
.cd { min-height: 100vh; background: #F4F6F9; color: #1F2A37; font-size: 17px; line-height: 1.5; }
.cd * { box-sizing: border-box; }
.cd button, .cd input, .cd textarea, .cd select { font: inherit; }
.cd :focus-visible { outline: 3px solid #2F6FB2; outline-offset: 2px; }
.cd-top { background: #1E3A5F; color: #fff; padding: 16px 20px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.cd-top h1 { margin: 0; font-size: 24px; }
.cd-top .team { opacity: 0.85; font-size: 16px; }
.cd-top .sp { flex: 1; }
.cd-btn { min-height: 44px; padding: 0 18px; border-radius: 10px; border: 1px solid #C9D3DF; background: #fff; color: #1E3A5F; font-weight: 700; cursor: pointer; }
.cd-btn.primary { background: #1E3A5F; color: #fff; border-color: #1E3A5F; }
.cd-top .cd-btn { background: #fff; color: #1E3A5F; border: none; }
.cd-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.cd-main { display: grid; grid-template-columns: minmax(320px, 420px) 1fr; gap: 0; min-height: calc(100vh - 76px); }
.cd-list { border-right: 1px solid #DCE3EB; background: #fff; }
.cd-tabs { display: flex; gap: 6px; padding: 12px; border-bottom: 1px solid #E6EBF1; flex-wrap: wrap; }
.cd-tab { min-height: 40px; padding: 0 14px; border-radius: 999px; border: 1px solid #C9D3DF; background: #fff; color: #334155; font-weight: 600; cursor: pointer; font-size: 15px; }
.cd-tab[aria-pressed="true"] { background: #1E3A5F; color: #fff; border-color: #1E3A5F; }
.cd-item { display: block; width: 100%; text-align: left; padding: 14px 16px; border: none; border-bottom: 1px solid #EEF2F6; background: #fff; cursor: pointer; }
.cd-item:hover { background: #F7F9FC; }
.cd-item[aria-current="true"] { background: #EAF1FA; box-shadow: inset 4px 0 0 #1E3A5F; }
.cd-item .nm { font-weight: 700; font-size: 18px; color: #1E3A5F; }
.cd-item .meta { font-size: 15px; color: #5B6B7D; margin-top: 2px; }
.cd-pill { display: inline-block; font-size: 14px; font-weight: 700; padding: 2px 10px; border-radius: 999px; margin-right: 6px; }
.cd-pill.late { background: #FDE8E3; color: #9A2E12; }
.cd-pill.today { background: #FFF4D6; color: #7A5300; }
.cd-pill.later { background: #E7F0FA; color: #1E4E86; }
.cd-pill.muted { background: #EEF1F4; color: #5B6B7D; }
.cd-pill.minor { background: #FFF1E6; color: #8A3B00; }
.cd-empty { padding: 32px 20px; color: #5B6B7D; text-align: center; }
.cd-detail { padding: 24px; max-width: 820px; }
.cd-card { background: #fff; border: 1px solid #DCE3EB; border-radius: 14px; padding: 18px 20px; margin-bottom: 16px; }
.cd-card h2 { margin: 0 0 4px; font-size: 24px; color: #1E3A5F; }
.cd-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 10px; }
.cd-label { display: block; font-weight: 700; font-size: 15px; color: #334155; margin: 14px 0 6px; }
.cd-field { width: 100%; padding: 12px 14px; border: 1px solid #C9D3DF; border-radius: 10px; background: #fff; font-size: 17px; color: #1F2A37; }
.cd-chip { min-height: 40px; padding: 0 14px; border-radius: 999px; border: 1px solid #C9D3DF; background: #fff; color: #334155; font-weight: 600; cursor: pointer; font-size: 15px; }
.cd-chip[aria-pressed="true"] { background: #E3EDF8; border: 2px solid #1E3A5F; color: #1E3A5F; }
.cd-guard { background: #FFF7ED; border: 1px solid #FDBA74; color: #7C2D12; border-radius: 10px; padding: 10px 14px; margin-top: 10px; font-size: 16px; }
.cd-tl { list-style: none; margin: 0; padding: 0; }
.cd-tl li { border-left: 3px solid #C9D3DF; padding: 2px 0 14px 14px; position: relative; }
.cd-tl li.kind-request { border-left-color: #2F6FB2; }
.cd-tl li.kind-status { border-left-color: #E2E8F0; }
.cd-tl .h { font-size: 15px; color: #5B6B7D; }
.cd-tl .h strong { color: #1E3A5F; }
.cd-tl .b { white-space: pre-wrap; margin-top: 2px; }
.cd-tl .lock { font-size: 13px; font-weight: 700; color: #6B21A8; background: #F3E8FF; padding: 1px 8px; border-radius: 999px; margin-left: 6px; }
.cd-err { color: #9A2E12; font-weight: 600; margin-top: 8px; }
.cd-back { display: none; }
.cd-check { display: flex; align-items: center; gap: 10px; margin-top: 12px; font-size: 16px; }
.cd-check input { width: 20px; height: 20px; }
@media (max-width: 820px) {
  .cd-main { grid-template-columns: 1fr; }
  .cd-main.has-sel .cd-list { display: none; }
  .cd-main:not(.has-sel) .cd-detail { display: none; }
  .cd-back { display: inline-flex; align-items: center; margin-bottom: 12px; }
  .cd-detail { padding: 16px; }
}
`;

const FOLLOW = [['Tomorrow', 1], ['3 days', 3], ['1 week', 7], ['2 weeks', 14]];

const CareDesk = () => {
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState('');
  const [view, setView] = useState('due');
  const [records, setRecords] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const st = view === 'closed' ? 'closed' : 'open';
    const r = await fetch(API + '/records?status=' + st, { headers: auth() });
    if (r.ok) setRecords((await r.json()).records);
  }, [view]);

  useEffect(() => {
    fetch(API + '/me', { headers: auth() }).then(async (r) => {
      if (r.status === 401) { window.location.href = '/login'; return; }
      if (!r.ok) { setDenied('The Care Desk is for chaplain teams. Ask your administrator to add your email to your team.'); return; }
      setMe(await r.json());
    });
  }, []);
  useEffect(() => { if (me) load(); }, [me, load]);

  const openRecord = useCallback(async (id) => {
    setSel(id); setAdding(false);
    const r = await fetch(API + '/records/' + id, { headers: auth() });
    if (r.ok) setDetail(await r.json());
    window.scrollTo(0, 0);
  }, []);
  const refresh = async () => { await load(); if (sel) await openRecord(sel); };

  if (denied) return <div className="cd"><style>{CSS}</style><div className="cd-top"><h1>Care Desk</h1></div><div className="cd-empty">{denied}</div></div>;
  if (!me) return <div className="cd"><style>{CSS}</style><div className="cd-top"><h1>Care Desk</h1></div><div className="cd-empty">Loading...</div></div>;

  const t = today();
  const due = records.filter(r => r.next_followup && r.next_followup <= t);
  const upcoming = records.filter(r => !r.next_followup || r.next_followup > t);
  const mine = records.filter(r => r.assigned_to === me.email);
  const shown = view === 'due' ? due : view === 'upcoming' ? upcoming : view === 'mine' ? mine : records;
  const tabs = [['due', 'Due now', due.length], ['upcoming', 'Upcoming', upcoming.length], ['mine', 'Mine', mine.length], ['closed', 'Closed', null]];
  const teamName = me.partners.map(p => p.name).join(' \u00b7 ');

  return (
    <div className="cd">
      <style>{CSS}</style>
      <header className="cd-top">
        <h1>Care Desk</h1>
        <span className="team">{teamName}</span>
        <span className="sp" />
        <button type="button" className="cd-btn" onClick={() => { setAdding(true); setSel('new'); setDetail(null); }}>+ Add a person</button>
      </header>
      <div className={'cd-main' + (sel ? ' has-sel' : '')}>
        <nav className="cd-list" aria-label="Care records">
          <div className="cd-tabs" role="group" aria-label="Show">
            {tabs.map(([k, l, n]) => (
              <button key={k} type="button" className="cd-tab" aria-pressed={view === k} onClick={() => setView(k)}>
                {l}{n !== null ? ' (' + n + ')' : ''}
              </button>
            ))}
          </div>
          {shown.length === 0 && <div className="cd-empty">{view === 'due' ? 'Nothing due. Every follow-up is up to date.' : 'No records here.'}</div>}
          {shown.map(r => {
            const d = dueInfo(r);
            return (
              <button key={r.id} type="button" className="cd-item" aria-current={sel === r.id} onClick={() => openRecord(r.id)}>
                <div className="nm">{r.person_name}</div>
                <div className="meta">
                  <span className={'cd-pill ' + d.tone}>{d.label}</span>
                  {r.is_minor ? <span className="cd-pill minor">Under 18</span> : null}
                </div>
                <div className="meta">{r.assigned_to ? 'With ' + short(r.assigned_to) : 'Unassigned'}{me.partners.length > 1 ? ' \u00b7 ' + (r.partner === 'rfc' ? 'Real Fellas' : 'Chaplain Care') : ''}</div>
              </button>
            );
          })}
        </nav>
        <main className="cd-detail">
          <button type="button" className="cd-btn cd-back" onClick={() => { setSel(null); setDetail(null); setAdding(false); }}>{'\u2190'} All records</button>
          {adding && <AddPerson me={me} onDone={async (id) => { await load(); if (id) openRecord(id); else { setAdding(false); setSel(null); } }} />}
          {!adding && !detail && <div className="cd-empty">Choose a person to see their care record.</div>}
          {!adding && detail && <Record me={me} data={detail} onChange={refresh} />}
        </main>
      </div>
    </div>
  );
};

const AddPerson = ({ me, onDone }) => {
  const [f, setF] = useState({ person_name: '', contact: '', note: '', partner: me.partners[0].id, next_followup: plusDays(1) });
  const [err, setErr] = useState('');
  const save = async () => {
    if (!f.person_name.trim()) { setErr('Add the person\u2019s name.'); return; }
    const r = await fetch(API + '/records', { method: 'POST', headers: auth(), body: JSON.stringify(f) });
    if (!r.ok) { setErr('Could not save. Try again.'); return; }
    onDone((await r.json()).id);
  };
  return (
    <div className="cd-card">
      <h2>Add a person</h2>
      <p style={{ margin: 0, color: '#5B6B7D' }}>For someone you met in person, by phone, or at the chapel.</p>
      {me.partners.length > 1 && (<>
        <label className="cd-label" htmlFor="cd-p">Team</label>
        <select id="cd-p" className="cd-field" value={f.partner} onChange={e => setF({ ...f, partner: e.target.value })}>
          {me.partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </>)}
      <label className="cd-label" htmlFor="cd-n">Name</label>
      <input id="cd-n" className="cd-field" value={f.person_name} onChange={e => setF({ ...f, person_name: e.target.value })} placeholder="First name is enough" />
      <label className="cd-label" htmlFor="cd-c">How to reach them</label>
      <input id="cd-c" className="cd-field" value={f.contact} onChange={e => setF({ ...f, contact: e.target.value })} placeholder="Phone, email, or where to find them" />
      <label className="cd-label" htmlFor="cd-o">What happened</label>
      <textarea id="cd-o" className="cd-field" rows={4} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} />
      <label className="cd-label">Follow up</label>
      <FollowPicker value={f.next_followup} onChange={v => setF({ ...f, next_followup: v })} />
      {err && <div className="cd-err">{err}</div>}
      <div className="cd-row"><button type="button" className="cd-btn primary" onClick={save}>Save</button><button type="button" className="cd-btn" onClick={() => onDone(null)}>Cancel</button></div>
    </div>
  );
};

const FollowPicker = ({ value, onChange }) => (
  <div className="cd-row" style={{ marginTop: 0 }}>
    {FOLLOW.map(([l, n]) => {
      const v = plusDays(n);
      return <button key={l} type="button" className="cd-chip" aria-pressed={value === v} onClick={() => onChange(v)}>{l}</button>;
    })}
    <input type="date" className="cd-field" style={{ width: 'auto', minHeight: '40px', padding: '6px 10px' }} value={value || ''} min={today()} onChange={e => onChange(e.target.value)} aria-label="Pick a date" />
    <button type="button" className="cd-chip" aria-pressed={value === ''} onClick={() => onChange('')}>None</button>
  </div>
);

const Record = ({ me, data, onChange }) => {
  const r = data.record;
  const [kind, setKind] = useState('call');
  const [body, setBody] = useState('');
  const [priv, setPriv] = useState(false);
  const nextDefault = (r.next_followup && r.next_followup > today()) ? r.next_followup : plusDays(7);
  const [next, setNext] = useState(nextDefault);
  const [close, setClose] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setBody(''); setPriv(false); setClose(false); setErr(''); setNext(nextDefault); }, [r.id, r.next_followup]); // eslint-disable-line react-hooks/exhaustive-deps
  const d = dueInfo(r);

  const save = async () => {
    if (!body.trim() && !close) { setErr('Write what happened.'); return; }
    setBusy(true); setErr('');
    const payload = { kind, body, private: priv, close };
    if (next) payload.next_followup = next; else payload.clear_followup = true;
    const res = await fetch(API + '/records/' + r.id + '/entries', { method: 'POST', headers: auth(), body: JSON.stringify(payload) });
    setBusy(false);
    if (!res.ok) { setErr('Could not save. Try again.'); return; }
    onChange();
  };
  const patch = async (p) => {
    await fetch(API + '/records/' + r.id, { method: 'PATCH', headers: auth(), body: JSON.stringify(p) });
    onChange();
  };

  return (<>
    <div className="cd-card">
      <h2>{r.person_name}</h2>
      <div><span className={'cd-pill ' + d.tone}>{d.label}</span>{r.is_minor ? <span className="cd-pill minor">Under 18</span> : null}</div>
      {r.contact && <div style={{ marginTop: 10 }}><strong>Reach them:</strong> {r.contact}</div>}
      {r.is_minor ? <div className="cd-guard"><strong>Under 18.</strong> Contact the parent or guardian first: {r.guardian || 'not given'}.</div> : null}
      <div className="cd-row">
        <label htmlFor="cd-as" style={{ fontWeight: 700 }}>With</label>
        <select id="cd-as" className="cd-field" style={{ width: 'auto', minHeight: 44 }} value={r.assigned_to || ''} onChange={e => patch({ assigned_to: e.target.value })}>
          <option value="">Unassigned</option>
          {me.team.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {r.assigned_to !== me.email && <button type="button" className="cd-btn" onClick={() => patch({ assigned_to: me.email })}>Take this</button>}
        {r.status === 'closed' && <button type="button" className="cd-btn" onClick={() => patch({ status: 'open' })}>Reopen</button>}
      </div>
    </div>

    {r.status !== 'closed' && (
      <div className="cd-card">
        <h2 style={{ fontSize: 20 }}>Log care</h2>
        <div className="cd-row" role="group" aria-label="Type">
          {KINDS.map(([k, l]) => <button key={k} type="button" className="cd-chip" aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>)}
        </div>
        <label className="cd-label" htmlFor="cd-body">What happened</label>
        <textarea id="cd-body" className="cd-field" rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="What you talked about, what they asked for, what you prayed about" />
        <label className="cd-check"><input type="checkbox" checked={priv} onChange={e => setPriv(e.target.checked)} /> Private note (only you can see it)</label>
        <label className="cd-label">Next follow-up</label>
        <FollowPicker value={next} onChange={setNext} />
        <label className="cd-check"><input type="checkbox" checked={close} onChange={e => setClose(e.target.checked)} /> Care is complete. Close this record.</label>
        {err && <div className="cd-err">{err}</div>}
        <div className="cd-row"><button type="button" className="cd-btn primary" disabled={busy} onClick={save}>{busy ? 'Saving...' : 'Save'}</button></div>
      </div>
    )}

    <div className="cd-card">
      <h2 style={{ fontSize: 20, marginBottom: 12 }}>History</h2>
      <ol className="cd-tl">
        {[...data.entries].reverse().map(e => (
          <li key={e.id} className={'kind-' + e.kind}>
            <div className="h"><strong>{KIND_LABEL[e.kind] || 'Note'}</strong> {'\u00b7'} {e.author_email === 'app' ? 'From the app' : short(e.author_email)} {'\u00b7'} {when(e.created_at)}{e.private ? <span className="lock">Private</span> : null}</div>
            <div className="b">{e.body}</div>
          </li>
        ))}
      </ol>
    </div>
  </>);
};

export default CareDesk;
